/**
 * Timeline Fullscreen - Alpine.js component for the "story" timeline layout.
 *
 * Closed state: a teaser strip sits in the normal content flow.
 * Opening expands a fixed overlay from the strip's rect to the full viewport
 * (animated with clip-path, so no layout thrash), locks page scroll and shows
 * one timeline entry per screen, navigated with Swiper.
 *
 * Navigation: prev/next buttons (labelled with the neighbouring date), the
 * segmented progress bar, touch swipe, arrow keys, Home/End, and one wheel
 * gesture per slide. Escape closes. Focus is trapped while open.
 *
 * Requires the shared Swiper bundle (lib/swiper.min.js). It is waited for, since scripts load async.
 */
import { whenSwiper } from '../lib/whenSwiper.js';

document.addEventListener('alpine:init', () => {
    Alpine.data('timelineFullscreen', (swiperId) => ({
        swiper: null,
        open: false,
        index: 0,
        total: 0,
        appearance: 'light',
        announcement: '',
        _wheelLock: false,
        _scrollY: 0,
        _lastFocused: null,
        _reduced: window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false,

        init() {
            this.total = this.$el.querySelectorAll('.timeline-fs-slide').length;
            console.log('[TimelineFullscreen] init:', { element: this.$el, swiperId, total: this.total, reducedMotion: this._reduced });

            // Keep the overlay honest if the viewport changes while it is open
            this._onResize = () => { if (this.open && this.swiper) this.swiper.update(); };
            window.addEventListener('resize', this._onResize, { passive: true });
        },

        /* ---------------------------------------------------------------- open */

        openStory(startIndex = 0) {
            if (this.open) return;

            const stage = this.$refs.stage;
            const trigger = this.$refs.trigger;
            if (!stage) {
                console.warn('[TimelineFullscreen] no stage ref - cannot open');
                return;
            }

            this._lastFocused = document.activeElement;
            this._lockScroll();

            // FLIP start: clip the fixed overlay to the teaser's current rect
            const r = (trigger || stage).getBoundingClientRect();
            const inset = [
                Math.max(0, r.top),
                Math.max(0, window.innerWidth - r.right),
                Math.max(0, window.innerHeight - r.bottom),
                Math.max(0, r.left)
            ];
            stage.style.clipPath = this._reduced ? 'inset(0)' : `inset(${inset[0]}px ${inset[1]}px ${inset[2]}px ${inset[3]}px)`;

            this.open = true;
            console.log('[TimelineFullscreen] opening from rect:', inset);

            this.$nextTick(() => {
                // Force a reflow so the start clip is committed before transitioning
                void stage.offsetWidth;
                stage.style.clipPath = 'inset(0)';

                // Runs immediately once Swiper is there, or as soon as it arrives.
                this._withSwiper(() => {
                    this.swiper.update();
                    this.swiper.slideTo(startIndex, 0);
                    this._syncFromSwiper();
                });

                const delay = this._reduced ? 0 : 400;
                setTimeout(() => {
                    this.$refs.close?.focus();
                    this.swiper?.update();
                }, delay);
            });
        },

        close() {
            if (!this.open) return;
            const stage = this.$refs.stage;
            const trigger = this.$refs.trigger;

            if (stage && trigger && !this._reduced) {
                const r = trigger.getBoundingClientRect();
                stage.style.clipPath = `inset(${Math.max(0, r.top)}px ${Math.max(0, window.innerWidth - r.right)}px ${Math.max(0, window.innerHeight - r.bottom)}px ${Math.max(0, r.left)}px)`;
            }

            const finish = () => {
                this.open = false;
                this._unlockScroll();
                if (stage) stage.style.clipPath = '';
                this._lastFocused?.focus?.();
                console.log('[TimelineFullscreen] closed');
            };

            this._reduced ? finish() : setTimeout(finish, 400);
        },

        /* ------------------------------------------------------------ scroll lock */

        _lockScroll() {
            this._scrollY = window.scrollY;
            const barWidth = window.innerWidth - document.documentElement.clientWidth;
            document.documentElement.style.overflow = 'hidden';
            document.body.style.overflow = 'hidden';
            document.body.style.touchAction = 'none';
            if (barWidth > 0) document.body.style.paddingRight = barWidth + 'px';
        },

        _unlockScroll() {
            document.documentElement.style.overflow = '';
            document.body.style.overflow = '';
            document.body.style.touchAction = '';
            document.body.style.paddingRight = '';
        },

        /* ---------------------------------------------------------------- swiper */

        /**
         * Call `callback` with the slider ready - creating it first when needed. The open animation
         * used to create the slider and use it in the same tick, and silently skipped the jump to the
         * chosen entry whenever the async Swiper bundle had not run yet.
         */
        _withSwiper(callback) {
            if (this.swiper) {
                callback();
                return;
            }
            const el = document.getElementById(swiperId);
            if (!el) {
                console.warn('[TimelineFullscreen] swiper element not found for', swiperId);
                return;
            }
            whenSwiper((Swiper, modules) => {
                if (!this.swiper) this._createSwiper(el, Swiper, modules);
                callback();
            });
        },

        _createSwiper(el, Swiper, modules) {
            this.swiper = new Swiper(el, {
                // lib/swiper.js no longer registers modules globally - each slider brings its own.
                modules: [modules.A11y],
                slidesPerView: 1,
                speed: this._reduced ? 0 : 600,
                grabCursor: true,
                watchSlidesProgress: true,
                a11y: { enabled: false }, // handled by the markup and this component
                on: {
                    slideChange: () => this._syncFromSwiper(),
                },
            });
            console.log('[TimelineFullscreen] swiper ready:', swiperId);
        },

        _syncFromSwiper() {
            if (!this.swiper) return;
            this.index = this.swiper.activeIndex;
            const slide = this.swiper.slides[this.index];
            this.appearance = slide?.dataset.appearance || 'light';
            this.announcement = `${this.index + 1} / ${this.total}`;
        },

        /* ------------------------------------------------------------ navigation */

        next() { this.swiper?.slideNext(); },
        prev() { this.swiper?.slidePrev(); },
        goTo(i) { this.swiper?.slideTo(i); },

        get isFirst() { return this.index === 0; },
        get isLast() { return this.index >= this.total - 1; },

        /** Label of the neighbouring entry, shown on the arrows like the reference does. */
        dateAt(i) {
            const slide = this.swiper?.slides?.[i];
            return slide?.dataset.date || '';
        },
        get prevDate() { return this.isFirst ? '' : this.dateAt(this.index - 1); },
        get nextDate() { return this.isLast ? '' : this.dateAt(this.index + 1); },

        /** One wheel gesture advances exactly one slide. */
        onWheel(event) {
            if (!this.open) return;
            event.preventDefault();
            if (this._wheelLock) return;

            const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
            if (Math.abs(delta) < 8) return;

            this._wheelLock = true;
            delta > 0 ? this.next() : this.prev();
            setTimeout(() => { this._wheelLock = false; }, this._reduced ? 120 : 650);
        },

        onKeydown(event) {
            if (!this.open) return;
            switch (event.key) {
                case 'Escape':    event.preventDefault(); this.close(); break;
                case 'ArrowRight':
                case 'ArrowDown': event.preventDefault(); this.next(); break;
                case 'ArrowLeft':
                case 'ArrowUp':   event.preventDefault(); this.prev(); break;
                case 'Home':      event.preventDefault(); this.goTo(0); break;
                case 'End':       event.preventDefault(); this.goTo(this.total - 1); break;
                case 'Tab':       this._trapFocus(event); break;
            }
        },

        /** Keeps Tab inside the dialog - the page behind it is inert while open. */
        _trapFocus(event) {
            const focusables = this.$refs.stage?.querySelectorAll(
                'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
            );
            if (!focusables || focusables.length === 0) return;

            const list = [...focusables].filter(el => el.offsetParent !== null);
            if (list.length === 0) return;

            const first = list[0];
            const last = list[list.length - 1];

            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        },

        destroy() {
            window.removeEventListener('resize', this._onResize);
            if (this.open) this._unlockScroll();
            this.swiper?.destroy();
            this.swiper = null;
        }
    }));
});
